/**
 * ReactionPanel is the user interface for displaying submitting reactions.
 */
import React, { useEffect, useRef, useState } from 'react'

import clsx from 'clsx'
import makeStyles from '@material-ui/core/styles/makeStyles'
import PropTypes from 'prop-types'

import { alpha } from '@material-ui/core'

import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import LinearProgress from '@material-ui/core/LinearProgress'
import TextField from '@material-ui/core/TextField'
import Tooltip from '@material-ui/core/Tooltip'

import DeleteIcon from '@material-ui/icons/Close'
import SubmitIcon from '@material-ui/icons/CheckRounded'

const useStyles = makeStyles(
  theme => ({
    root: {
      alignItems: 'stretch',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto'
    },

    sentimentsContainer: {
      display: 'flex',
      overflow: 'auto'
    },

    reactionButton: {},

    reactionButtonLabel: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column'
    },

    reactionVisual: {
      alignItems: 'center',
      display: 'flex'
    },

    reactionLabel: {},

    selectedSentiment: {
      backgroundColor: alpha(theme.palette.secondary.light, 0.125),

      '& $reactionLabel': {
        fontWeight: theme.typography.fontWeightMedium
      }
    },

    commentContainer: {
      alignItems: 'center',
      columnGap: theme.spacing(1),
      display: 'flex',
      padding: theme.spacing(1)
    },

    showCommentButton: {
      flexGrow: 1
    },

    showCommentIcon: {
      color: theme.palette.text.secondary
    },

    commentActions: {
      display: 'flex',
      gap: theme.spacing(0.5)
    },

    commentActionIcon: {
      height: theme.spacing(2),
      width: theme.spacing(2)
    },

    dismissCommentButton: {},
    dismissCommentIcon: {},

    submitCommentButton: {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.common.white,

      '&:hover': {
        backgroundColor: theme.palette.success.dark
      }
    },

    submitCommentIcon: {},

    deleteButton: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.common.white,

      '&:hover': {
        backgroundColor: theme.palette.error.dark
      }
    },

    deleteIcon: {},

    collapse: {
      flexGrow: 1
    },

    commentFieldContainer: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      position: 'relative'
    },

    comment: {},

    progress: {
      borderBottomLeftRadius: theme.spacing(0.5),
      borderBottomRightRadius: theme.spacing(0.5),
      bottom: 0,
      height: theme.spacing(0.25),
      left: 0,
      position: 'absolute',
      right: 0
    },

    commentInput: {
      padding: theme.spacing(1),

      '&::placeholder': {
        fontStyle: 'italic'
      }
    }
  }),
  {
    name: 'ReactionPanel'
  }
)

const MAX_COMMENT_LENGTH = 140

const ReactionPanel = props => {
  const { className, sentiments, initialReaction, onSubmit } = props
  const { sentiment: initialSentiment, comment: initialComment } = initialReaction ?? {}

  const commentField = useRef()

  const [sentiment, setSentiment] = useState(initialSentiment)
  const [comment, setComment] = useState(initialComment ?? '')

  const handleCommentChange = event => setComment(event.target.value)

  const selectHandler = sentiment => event => setSentiment(sentiment)

  const submitHandler = sentiment => event => {
    if (onSubmit) {
      onSubmit({
        sentiment,
        comment
      })
    }
  }

  const handleSubmitWithInitialSentiment = event => {
    event.preventDefault()

    // Safari workaround: for some reason we need to release keyboard focus because otherwise
    // the form submit scrolls the document to the bottom 🤷🏽‍♂️
    commentField.current?.querySelector('input')?.blur()

    if (onSubmit) {
      onSubmit({ sentiment, comment })
    }
  }

  const handleDelete = event => {
    if (onSubmit) {
      onSubmit({}) // No sentiment and no comment: this is equivalent to a deletion.
    }
  }

  useEffect(() => {
    if (sentiment && commentField.current) {
      // We access the comment field directly to avoid using comment state as a dependency.
      const commentInput = commentField.current.querySelector('input')
      if (commentInput.value) {
        commentInput.select()
      } else {
        commentInput.focus()
      }
    }
  }, [sentiment])

  const classes = useStyles(props)
  return (
    <form className={clsx(classes.root, className)} onSubmit={handleSubmitWithInitialSentiment}>
      <section className={classes.sentimentsContainer}>
        {sentiments.map(({ id, visual, label }) => (
          <Button
            key={id}
            classes={{
              root: classes.reactionButton,
              label: classes.reactionButtonLabel,
              textSecondary: classes.selectedSentiment
            }}
            color={sentiment === id ? 'secondary' : undefined}
            onClick={selectHandler(id)}
            onDoubleClick={submitHandler(id)}
          >
            <span className={classes.reactionVisual}>{visual}</span>
            <span className={classes.reactionLabel}>{label}</span>
          </Button>
        ))}
      </section>

      <section className={classes.commentContainer}>
        <span className={classes.commentFieldContainer}>
          <TextField
            ref={commentField}
            className={classes.comment}
            disabled={!sentiment}
            inputProps={{ className: classes.commentInput, maxLength: MAX_COMMENT_LENGTH }}
            fullWidth
            placeholder={sentiment ? undefined : 'Select emoji to leave a comment'}
            variant="outlined"
            value={comment}
            onChange={handleCommentChange}
          />

          <LinearProgress className={classes.progress} variant="determinate" value={(comment.length * 100) / 140} />
        </span>

        <section className={classes.commentActions}>
          <Tooltip arrow title="Submit your reaction">
            {/* No CSS below; span is purely a wrapper to facilitate tooltip. */}
            <span>
              <IconButton className={classes.submitCommentButton} size="small" type="submit" disabled={!sentiment}>
                <SubmitIcon className={clsx(classes.commentActionIcon, classes.submitCommentIcon)} />
              </IconButton>
            </span>
          </Tooltip>

          {initialSentiment && (
            // When there is an initial sentiment, that means this is a pre-existing reaction
            // so we have the option of deleting it.
            <Tooltip
              arrow
              title={
                <span>
                  Delete the reaction <em>entirely</em>
                </span>
              }
            >
              <IconButton className={classes.deleteButton} size="small" onClick={handleDelete}>
                <DeleteIcon className={clsx(classes.commentActionIcon, classes.deleteIcon)} />
              </IconButton>
            </Tooltip>
          )}
        </section>
      </section>
    </form>
  )
}

ReactionPanel.propTypes = {
  className: PropTypes.string,

  sentiments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string, // Unique identifier for the sentiment.
      visual: PropTypes.node // Visual representation of the sentiment.
    })
  ),

  initialReaction: PropTypes.shape({
    sentiment: PropTypes.string, // Current sentiment to select.
    comment: PropTypes.string // Current comment to show.
  }),

  onSubmit: PropTypes.func, // Function to call after the user finishes entering their reaction.
  onToggleComment: PropTypes.func // Function to call after comment visibility changes.
}

export default ReactionPanel
